import React, { useState, useRef } from "react";
import TrackListTable from "../tables/TrackListTable";
import { errorHandler } from "../ErrorHandler";
import { successMsg } from "../SuccessHandler";
import NewTrackModal from "./NewTrackModal";
import LastChangesModal from "../lastChanges/LastChangesModal";
import ErrorCodes from "../../constants/ErrorCodes";
import SiteConfig from "../../constants/SiteConfig";
import { useUpdateFunctions, useInsertFunctions } from "../../functions";
import ConfirmDialog from "../utilities/ConfirmDialog";
import Rights from "../../constants/Rights";

const TrackListModal = ({
  trackList,
  setTrackList,
  gameProviders,
  trackCategoryCbx,
  data,
  getTracks,
  lastChanges,
  setLastChanges,
  lastChangesInfo,
  setLastChangesInfo,
  fieldChangesInfo,
  setFieldChangesInfo,
  getFieldChanges,
  openLastChanges,
  trackOptionSelected,
  setTrackOptionSelected,
  trackName,
  setTrackName,
  setRefresh,
  setChangeData,
  shouldFocusLastTrack,
  setShouldFocusLastTrack,
  countries,
  allStates,
  setSessionNoLongerExists,
  publicIP,
  loadingTracks,
  systemPreferences,
}) => {
  //Constants for filters
  const [filterInput, setFilterInput] = useState("");
  const [selectedTrackType, setSelectedTrackType] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("");
  const divRef = useRef(null);
  const closeButtonRef = useRef();
  const [hasChanges, setHasChanges] = useState(false);
  const [columns, setColumns] = useState([]);
  const { updateTracks } = useUpdateFunctions();
  const { insertTrack } = useInsertFunctions();
  const userRightsList = JSON.parse(localStorage.getItem("UserRights")) || [];
  const [filteredData, setFilteredData] = useState([]);

  //Hook to insert new track
  const [newTrack, setNewTrack] = useState({
    LogonSessionID: "",
    Enabled: true,
    TrackName: "",
    TrackCode: "",
    ShortName: "",
    TrackTimeZone: "Dateline Standard Time",
    DefaultCloseMethod: 1, //Feed
    DefaultPostCutoff: 0,
    TrackType: 0, //Thoroughbred
    IdTrackCategory: 1, //Category A
    LastModification: "",
    IdGameProvider: 1, //DGS
    SpecialEvent: false,
    AmtoteTrackCode: "",
    CountryCode: "",
    StateCode: "",
    ScoringRule: 0,
    errors: {},
  });

  const tableRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  let lastChangesBtn;

  const openTrackChanges = () => {
    if (trackOptionSelected === "") {
      errorHandler("Select a Track");
      return;
    }

    openLastChanges("TRACKLOG", trackOptionSelected, 0, trackName);
    setIsModalOpen(true);
  };

  lastChangesBtn = (
    <button
      type="button"
      className="btn btn-dark"
      onClick={() => openTrackChanges()}
    >
      Last Changes
    </button>
  );

  //Clears checkboxes and filter input fields when closing the Track List modal
  const CleanFilters = () => {
    setFilterInput("");
    setSelectedTrackType("");
    setSelectedProvider("");

    const div = divRef.current;
    if (div) {
      div.scrollTop = 0;
    }

    // Clean the shadow of the new row
    if (filteredData.length > 0) {
      const rows = tableRef.current.getElementsByTagName("tr");
      const lastRow = rows[rows.length - 1];
      lastRow.style.backgroundColor = null;
    }
  };

  // Update Tracks Function
  const updateTracksList = (tracks, close) => {
    if (hasChanges) {
      let hasError = false;

      tracks.forEach((track) => {
        [track].forEach((item) => {
          if (!item.TrackCode) {
            hasError = true;
          }
          if (!item.TrackName) {
            hasError = true;
          }
          if (item.DefaultPostCutoff < 0) {
            hasError = true;
          }
        });
      });
      if (hasError && close) {
        closeButtonRef.current.click();
      } else if (!hasError) {
        let msgCancel;
        if (close) {
          msgCancel = "Exit anyway";
        } else {
          msgCancel = "Cancel";
        }
        ConfirmDialog(`Changes will be saved!`, msgCancel).then(
          async (result) => {
            if (result.isConfirmed) {
              let InParams = [];
              tracks.forEach((element) => {
                if (element.Change) {
                  InParams.push({
                    ...element,
                    DefaultPostCutoff: parseInt(element.DefaultPostCutoff),
                  });
                }
              });

              if (InParams.length === 0) {
                InParams = {};
              }

              const res = await updateTracks(
                {
                  Tracks: InParams,
                  PhoneLine: SiteConfig.LOGIN_PHONE_LINE,
                  IP: publicIP,
                },
                data.LogonSessionID,
              );

              if (res?.error) {
                if (
                  res.Result === ErrorCodes.InvalidSession ||
                  res.Result === ErrorCodes.SessionNoLongerExists
                ) {
                  setSessionNoLongerExists(true);
                }
                return;
              } else {
                successMsg("Update Tracks");
                setHasChanges(false);
                if (close) {
                  closeButtonRef.current.click();
                  const div = divRef.current;
                  if (div) {
                    div.scrollTop = 0;
                  }
                }
              }
            } else {
              if (close) {
                closeButtonRef.current.click();
                const div = divRef.current;
                if (div) {
                  div.scrollTop = 0;
                }
              }
            }
          },
        );
      }
    }
  };

  //Insert Track Function
  const handleInsertTrack = async (newTrack) => {
    const _errors = {};
    if (!newTrack.TrackCode) _errors.TrackCode = "Track Code is required";
    if (!newTrack.TrackName) _errors.TrackName = "Track Name is required";
    if (newTrack.DefaultPostCutoff === "" || newTrack.DefaultPostCutoff < 0)
      _errors.DefaultPostCutoff = "Past Post cannot be negative or empty";

    setNewTrack((prev) => ({
      ...prev,
      errors: _errors,
    }));
    if (Object.keys(_errors).length === 0) {
      const res = await insertTrack(
        {
          ...newTrack,
          PhoneLine: SiteConfig.LOGIN_PHONE_LINE,
          IP: publicIP,
        },
        data.LogonSessionID,
      );

      if (res?.error) {
        if (
          res.Result === ErrorCodes.InvalidSession ||
          res.Result === ErrorCodes.SessionNoLongerExists
        ) {
          setSessionNoLongerExists(true);
        }
        return;
      } else {
        successMsg("Save Track");
        getTracks(true);
        setRefresh(true);
        setChangeData(true);
        setNewTrack({
          LogonSessionID: "",
          Enabled: true,
          TrackName: "",
          TrackCode: "",
          ShortName: "",
          TrackTimeZone: "Dateline Standard Time",
          DefaultCloseMethod: 1,
          DefaultPostCutoff: 0,
          TrackType: 0,
          IdTrackCategory: 1,
          LastModification: "",
          IdGameProvider: 1,
          SpecialEvent: false,
          AmtoteTrackCode: "",
          CountryCode: "",
          StateCode: "",
          ScoringRule: 0,
          errors: {},
        });
        setShouldFocusLastTrack(false);
      }
    }
  };

  // This function is executed when closing the modal from the close button or from x
  const close = () => {
    if (hasChanges) {
      updateTracksList(trackList, true);
      setHasChanges(false);
      setRefresh(false);
      setChangeData(false);
    } else {
      closeButtonRef.current.click();
      CleanFilters();
      setRefresh(false);
      setChangeData(false);
      setColumns([]);
    }
    setTrackOptionSelected("");
    setTrackName("");
  };

  const refreshTracks = () => {
    getTracks();
    setRefresh(true);
    setChangeData(false);
  };

  return (
    <>
      <div
        className="modal fade"
        id="trackListModal"
        role="dialog"
        aria-hidden="false"
        data-keyboard="false"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header headerModals">
              <h5 className="modal-title" id="trackListLabel">
                Track List
              </h5>
              <span
                data-dismiss="modal"
                ref={closeButtonRef}
                style={{ cursor: "inherit" }}
              ></span>
              <button type="button" className="close" onClick={() => close()}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {trackList ? (
                <TrackListTable
                  trackList={trackList}
                  setTrackList={setTrackList}
                  gameProviders={gameProviders}
                  trackCategoryCbx={trackCategoryCbx}
                  filterInput={filterInput}
                  setFilterInput={setFilterInput}
                  selectedTrackType={selectedTrackType}
                  setSelectedTrackType={setSelectedTrackType}
                  selectedProvider={selectedProvider}
                  setSelectedProvider={setSelectedProvider}
                  data={data}
                  divRef={divRef}
                  setHasChanges={setHasChanges}
                  shouldFocusLastTrack={shouldFocusLastTrack}
                  setShouldFocusLastTrack={setShouldFocusLastTrack}
                  tableRef={tableRef}
                  trackOptionSelected={trackOptionSelected}
                  setTrackOptionSelected={setTrackOptionSelected}
                  setTrackName={setTrackName}
                  setChangeData={setChangeData}
                  countries={countries}
                  allStates={allStates}
                  setSessionNoLongerExists={setSessionNoLongerExists}
                  columns={columns}
                  setColumns={setColumns}
                  publicIP={publicIP}
                  loadingTracks={loadingTracks}
                  filteredData={filteredData}
                  setFilteredData={setFilteredData}
                  systemPreferences={systemPreferences}
                />
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    color: "#808080",
                    minHeight: "300px",
                  }}
                >
                  <span className="parpadea">Loading...</span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <div className="col-sm-6">
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={() => refreshTracks()}
                >
                  Refresh
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-toggle="modal"
                  data-target="#newTrackModal"
                >
                  New Track
                </button>
                {userRightsList.find(
                  (right) => right.FormName === Rights.FRM_LAST_CHANGES,
                ) && lastChangesBtn}
              </div>
              <div
                className="col-sm-6"
                style={{ textAlign: "right", paddingRight: "0px" }}
              >
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={() => updateTracksList(trackList, false)}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={close}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <NewTrackModal
        newTrack={newTrack}
        setNewTrack={setNewTrack}
        trackCategoryCbx={trackCategoryCbx}
        gameProviders={gameProviders}
        data={data}
        handleInsertTrack={handleInsertTrack}
        countries={countries}
      />

      {isModalOpen && (
        <LastChangesModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          lastChanges={lastChanges}
          setLastChanges={setLastChanges}
          lastChangesInfo={lastChangesInfo}
          getFieldChanges={getFieldChanges}
          fieldChangesInfo={fieldChangesInfo}
          setLastChangesInfo={setLastChangesInfo}
          setFieldChangesInfo={setFieldChangesInfo}
        />
      )}
    </>
  );
};

export default TrackListModal;
